import { useForm } from "@tanstack/react-form";
import { toast } from "sonner";
import { APPOINTMENT_NOTES_TOAST } from "@/lib/appointments/appointment-toast";

import { DataError } from "@/components/common/error/data-error";
import { Button } from "@/components/ui/button";
import {
  DialogClose,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { getClientAppointmentUpcomingFormValues } from "@/default-form-values/clients/default-client-appointment-form-values";
import {
  useGetClientUpcomingAppointments,
  useUpdateAppointment,
} from "@/hooks/appointments/use-appointments";
import { emptyToNull } from "@/mappers/clients/client-form-to-request";
import {
  clientAppointmentFormKey,
  earliestServiceStartTime,
  sortAppointmentsSoonestFirst,
  upcomingVisitLines,
} from "@/mappers/clients/client-appointment-display";
import type { Appointment } from "@/types/appointments/api-types/appointment-types";
import { clientAppointmentUpcomingSchema } from "@/zod-schemas/clients/client-appointment-upcoming-schema";
import { ClientAppointmentVisitBlock } from "./client-appointment-visit-block";

interface ClientAppointmentUpcomingTabProps {
  clientId: number;
  onOpenChange: (open: boolean) => void;
}

export function ClientAppointmentUpcomingTab({
  clientId,
  onOpenChange,
}: ClientAppointmentUpcomingTabProps) {
  const { items, isPending, isError, isSuccess } =
    useGetClientUpcomingAppointments(clientId, true);

  if (isPending || !isSuccess) {
    if (isError) {
      return <DataError topic="upcoming appointments" />;
    }
    return (
      <div className="flex flex-1 items-center justify-center py-10">
        <Spinner className="size-6" />
      </div>
    );
  }

  const appointments = sortAppointmentsSoonestFirst(items);
  if (appointments.length === 0) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <p className="flex-1 py-6 text-sm text-muted-foreground">
          No upcoming appointments
        </p>
        <DialogFooter className="shrink-0 border-t pt-4">
          <DialogClose asChild>
            <Button variant="outline" className="w-24">
              Cancel
            </Button>
          </DialogClose>
        </DialogFooter>
      </div>
    );
  }

  return (
    <UpcomingNotesForm
      key={clientAppointmentFormKey(
        appointments.map((appointment) => appointment.id)
      )}
      appointments={appointments}
      onOpenChange={onOpenChange}
    />
  );
}

interface UpcomingNotesFormProps {
  appointments: Appointment[];
  onOpenChange: (open: boolean) => void;
}

function UpcomingNotesForm({
  appointments,
  onOpenChange,
}: UpcomingNotesFormProps) {
  const updateAppointment = useUpdateAppointment();
  const loadedValues = getClientAppointmentUpcomingFormValues(appointments);

  const form = useForm({
    defaultValues: loadedValues,
    validators: {
      onSubmit: clientAppointmentUpcomingSchema,
    },
    onSubmit: async ({ value }) => {
      const validated = clientAppointmentUpcomingSchema.parse(value);

      try {
        for (const item of validated.items) {
          const original = loadedValues.items.find(
            (loaded) => loaded.id === item.id
          );
          if (!original) continue;

          const notes = emptyToNull(item.notes);
          if (emptyToNull(original.notes) === notes) continue;

          await updateAppointment.mutateAsync({
            id: item.id,
            data: { notes },
          });
        }
        toast.success(APPOINTMENT_NOTES_TOAST.success);
      } catch {
        toast.error(APPOINTMENT_NOTES_TOAST.error);
        return;
      }
      form.reset(validated);
    },
  });

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <form
        id="client-appointment-upcoming-form"
        onSubmit={(event) => {
          event.preventDefault();
          form.handleSubmit();
        }}
        className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto"
      >
        {appointments.map((appointment, index) => (
          <div key={appointment.id}>
            {index > 0 ? <Separator className="mb-6" /> : null}
            <ClientAppointmentVisitBlock
              appointmentDate={appointment.appointment_date}
              startTime={earliestServiceStartTime(appointment.services)}
              status={appointment.status}
              lines={upcomingVisitLines(appointment)}
            >
              <FieldGroup className="mt-4">
                <form.Field
                  name={`items[${index}].notes`}
                  children={(field) => {
                    const isInvalid =
                      field.state.meta.isTouched && !field.state.meta.isValid;
                    return (
                      <Field data-invalid={isInvalid}>
                        <FieldLabel htmlFor={field.name}>Notes</FieldLabel>
                        <Textarea
                          id={field.name}
                          name={field.name}
                          value={field.state.value}
                          onBlur={field.handleBlur}
                          onChange={(e) => field.handleChange(e.target.value)}
                          aria-invalid={isInvalid}
                          rows={2}
                        />
                        {isInvalid && (
                          <FieldError errors={field.state.meta.errors} />
                        )}
                      </Field>
                    );
                  }}
                />
              </FieldGroup>
            </ClientAppointmentVisitBlock>
          </div>
        ))}
      </form>
      <DialogFooter className="shrink-0 border-t pt-4">
        <DialogClose asChild>
          <Button
            type="button"
            variant="outline"
            className="w-24"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
        </DialogClose>
        <form.Subscribe
          selector={(state) => [state.isDirty, state.isSubmitting] as const}
          children={([isDirty, isSubmitting]) => (
            <Button
              type="submit"
              form="client-appointment-upcoming-form"
              className="w-24"
              disabled={!isDirty || isSubmitting}
            >
              Save
            </Button>
          )}
        />
      </DialogFooter>
    </div>
  );
}
